import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  fetchCompanies,
  getCompanyWebsites,
  getEvaluationStatus,
  checkProgress,
  stopWebsite,
  uploadWebsite,
  deleteWebsite,
  stopDocument,
  uploadFile,
  createCompany,
  exportCompany,
  importCompany,
  deleteDocument,
  getDownloadableFile,
  getScrappedWebsiteContent,
  updateAboutCompany,
  deleteSingleCompany,
  updateSingleCompany,
  getSingleCompany,
  getCompaniesAll,
  getStorageQuota,
  workspaceShareCompany,
  rescrapeWebsite,
  detachDocumentFromQuestion,
} from "../Api/company";
import {
  fetchDocuments,
  smartSearchDocuments,
  fetchDocumentLogs,
  fetchKnowledgeBaseDocumentLogs,
} from "../Api/documents";
import {
  fetchCoverSheet,
  uploadCoverSheet,
  updateCoverSheet,
} from "../Api/documents";
import { ProcessingStatus, EvaluationStatus } from "../Enums/companyStatus";
import {
  CompanyInfo,
  Document,
  DownloadFilteredContentParams,
  StorageQuota,
} from "../Models/company";
import { fetchFilteredContent } from "../Api/filteredcontent";
import { AxiosResponse } from "axios";

export const useCompanies = (
  page: number,
  page_size: number,
  sort_order: string,
  sort_direction: string,
  search: string,
  workspace_shared_only?: boolean,
) => {
  return useQuery({
    queryKey: [
      "companies",
      page,
      page_size,
      sort_order,
      sort_direction,
      search,
      workspace_shared_only,
    ],
    queryFn: () =>
      fetchCompanies(
        page,
        page_size,
        sort_order,
        sort_direction,
        search,
        workspace_shared_only,
      ),
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCompaniesAll = (
  page: number,
  page_size: number,
  sort_order: string,
  search: string,
  user_id?: number,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["companiesAll", page, page_size, sort_order, search, user_id],
    queryFn: () =>
      getCompaniesAll(page, page_size, sort_order, search, user_id),
    staleTime: 2 * 60 * 1000,
    gcTime: 0,
    placeholderData: undefined,
    enabled: options?.enabled ?? true,
  });
};

export const useWebsites = (
  companyId: string,
  page: number,
  page_size: number,
  sort_order: string,
  sort_direction: string,
  search: string,
  open: boolean,
) => {
  return useQuery({
    queryKey: [
      "websites",
      companyId,
      page,
      page_size,
      sort_order,
      sort_direction,
      search,
    ],
    queryFn: () =>
      getCompanyWebsites(
        companyId,
        page,
        page_size,
        sort_order,
        sort_direction,
        search,
      ),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      if (!open || !query.state.data) return false;

      const websitesPending = query.state.data.websites.some(
        (website) =>
          website.status === ProcessingStatus.WAITING ||
          website.status === ProcessingStatus.IN_PROGRESS,
      );

      if (!websitesPending) return false;

      return 2000;
    },
  });
};

export const useDocuments = (
  companyId: string,
  page: number,
  page_size: number,
  sort_order: string,
  sort_direction: string,
  getAll: boolean = false,
) => {
  return useQuery({
    queryKey: [
      "documents",
      companyId,
      getAll ? "all" : page,
      page_size,
      sort_order,
      sort_direction,
    ],
    queryFn: () =>
      fetchDocuments(
        companyId,
        page,
        page_size,
        sort_order,
        sort_direction,
        getAll,
      ),
    placeholderData: !getAll ? keepPreviousData : undefined,
    refetchInterval: (query) => {
      if (getAll) return false;

      const documentsPending = query?.state?.data?.documents.some(
        (doc: Document) =>
          doc.status === ProcessingStatus.WAITING ||
          doc.status === ProcessingStatus.IN_PROGRESS,
      );

      if (!documentsPending) return false;

      return 2000;
    },
  });
};

export const useEvaluationStatus = (companyId: string | undefined) => {
  return useQuery({
    queryKey: ["companyStatus", companyId],
    queryFn: () => {
      if (!companyId) {
        return Promise.reject(new Error("Invalid companyId"));
      }
      return getEvaluationStatus(companyId);
    },
    enabled: !!companyId,
    refetchInterval: 5000,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useCheckProgress = (evaluationId?: string) => {
  return useQuery({
    queryKey: ["checkProgress", evaluationId],
    queryFn: () => checkProgress(evaluationId),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useStopWebsite = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (websiteId: number) => stopWebsite(websiteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["websites", companyId] });
    },
  });
};

export const useUploadWebsite = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      websiteName,
      websiteAddress,
    }: {
      websiteName: string;
      websiteAddress: string;
    }) => uploadWebsite(companyId, websiteName, websiteAddress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["websites", companyId] });
    },
  });
};

export const useDeleteWebsite = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (websiteId: number) => deleteWebsite(websiteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["websites", companyId] });
    },
  });
};

export const useRescrapeWebsite = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (websiteId: number) => rescrapeWebsite(websiteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["websites", companyId] });
    },
  });
};

export const useDownloadWebsiteContent = () => {
  return useMutation({
    mutationFn: async ({ websiteId }: { websiteId: number }) =>
      getScrappedWebsiteContent(websiteId),
  });
};

export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      about,
      industry,
      location,
      icon_id,
    }: {
      name: string;
      about: string;
      industry: string;
      location: string;
      icon_id: number;
    }) => createCompany(name, about, industry, location, icon_id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["companies"],
        refetchType: "all",
      });
    },
  });
};

export const useUploadDocument = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => uploadFile(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};

export const useDeleteDocument = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: number) => deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};

export const useDownloadDocument = () => {
  return useMutation({
    mutationFn: async ({ documentId }: { documentId: number }) =>
      getDownloadableFile(documentId),
  });
};

export const useExportCompany = () => {
  return useMutation({
    mutationFn: async ({ companyId }: { companyId: string }) =>
      exportCompany(companyId),
  });
};

export const useImportCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => importCompany(file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["companies"],
        refetchType: "all",
      });
    },
  });
};

export const useStopDocument = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: number) => stopDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};

export const useCoverSheet = (
  companyId: string | undefined,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["coverSheet", companyId],
    queryFn: () => fetchCoverSheet(companyId!),
    enabled: !!companyId && enabled,
  });
};

export const useUploadCoverSheet = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadCoverSheet(companyId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coverSheet", companyId] });
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};

export const useUpdateCoverSheet = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, string>) =>
      updateCoverSheet(companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coverSheet", companyId] });
    },
  });
};

export const useDownloadFilteredContent = () => {
  return useMutation<AxiosResponse, Error, DownloadFilteredContentParams>({
    mutationFn: (variables: {
      entityType: string;
      entityId: string;
      questionListId: string | number;
    }) =>
      fetchFilteredContent(
        variables.entityType,
        variables.entityId,
        variables.questionListId,
      ),
  });
};

export const useUpdateAboutCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      evaluationId,
      answers,
    }: {
      companyId: string;
      evaluationId: string;
      answers: unknown;
    }) =>
      updateAboutCompany(companyId, { evaluation_id: evaluationId, answers }),
    onSuccess: (_, { companyId, evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: ["companyStatus", companyId] });
      queryClient.invalidateQueries({
        queryKey: ["singleEvaluation", Number(evaluationId)],
      });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["checkProgress"] });
    },
  });
};

export const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => deleteSingleCompany(companyId),
    onSuccess: (_, companyId) => {
      queryClient.invalidateQueries({
        queryKey: ["companies"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["companyStatus", companyId] });
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
      queryClient.invalidateQueries({ queryKey: ["websites", companyId] });
      queryClient.invalidateQueries({ queryKey: ["coverSheet", companyId] });
    },
  });
};

export const useDeleteSingleCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => deleteSingleCompany(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

export const useUpdateSingleCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      name,
      about,
      location,
      industry,
      icon_id,
    }: {
      id: string;
      name: string;
      about: string;
      location: string;
      industry: string;
      icon_id: number;
    }) => updateSingleCompany(id, name, about, location, industry, icon_id),
    onSettled: (_, __, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["companyStatus", id] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["singleCompany", id] });
    },
  });
};

export const useSingleCompany = (companyId?: string) => {
  return useQuery<CompanyInfo, Error>({
    queryKey: ["singleCompany", companyId],
    queryFn: () => getSingleCompany(companyId!),
    enabled: !!companyId,
    refetchInterval: (query) => {
      if (!query.state.data) return false;

      const hasInProgressEvaluations = query.state.data.evaluations?.some(
        (evaluation) =>
          evaluation.status === EvaluationStatus.EVALUATION_IN_PROGRESS ||
          evaluation.status ===
            EvaluationStatus.GATHERING_DIGITAL_PERFORMANCE ||
          evaluation.status === EvaluationStatus.FETCHING_COMPANY_DETAILS ||
          evaluation.status ===
            EvaluationStatus.PROCESSING_COMPANY_INFORMATIONS ||
          evaluation.status === EvaluationStatus.GRADING_COMPANY ||
          evaluation.status === EvaluationStatus.INITIALIZED,
      );

      if (!hasInProgressEvaluations) return false;

      return 2000;
    },
  });
};

export const useSmartSearch = (
  companyId: string,
  search: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["smartSearch", companyId, search],
    queryFn: () => smartSearchDocuments(companyId, search),
    enabled: enabled && !!search && !!companyId,
  });
};

export const useDocumentLogs = (
  documentId: number | undefined,
  open: boolean,
) => {
  return useQuery({
    queryKey: ["documentLogs", documentId],
    queryFn: () => fetchDocumentLogs(documentId!),
    enabled: !!documentId && open,
    gcTime: 0,
    staleTime: 0,
  });
};

export const useKnowledgeBaseDocumentLogs = (
  documentId: number | undefined,
  open: boolean,
) => {
  return useQuery({
    queryKey: ["kbDocumentLogs", documentId],
    queryFn: () => fetchKnowledgeBaseDocumentLogs(documentId!),
    enabled: !!documentId && open,
    gcTime: 0,
    staleTime: 0,
  });
};

export const useStorageQuota = (companyId?: string) => {
  return useQuery<StorageQuota, Error>({
    queryKey: ["storageQuota", companyId],
    queryFn: () => {
      if (!companyId) throw new Error("Invalid companyId");
      return getStorageQuota(companyId);
    },
    enabled: !!companyId,
    gcTime: 0,
    staleTime: 0,
  });
};

export const useWorkspaceShareCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) => workspaceShareCompany(companyId),

    onSuccess: (_, companyId) => {
      queryClient.invalidateQueries({
        queryKey: ["companies"],
      });

      queryClient.invalidateQueries({
        queryKey: ["singleCompany", companyId],
      });
    },
  });
};

export const useDetachDocumentFromQuestion = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: number) => detachDocumentFromQuestion(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};
